import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Check, ArrowLeft, ArrowRight, ShieldCheck, Flame, Zap } from "lucide-react";
import PublicLayout from "../components/PublicLayout";

export default function PricingPage() {
    const { user } = useAuth();
    const [upgrading, setUpgrading] = useState(false);

    const handleUpgrade = async (planId) => {
        if (!user) {
            toast.error("Please login or create an account to subscribe.");
            return;
        }
        setUpgrading(true);
        try {
            const { data } = await api.post("/auth/billing/checkout", { plan_id: planId });
            if (data.checkout_url) {
                // Redirect user to Freemius secure hosted checkout dialog
                window.location.href = data.checkout_url;
            } else {
                toast.error("Failed to generate checkout link.");
            }
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to initialize checkout gateway.");
        } finally {
            setUpgrading(false);
        }
    };

    return (
        <PublicLayout>

            {/* Hero Section */}
            <section className="max-w-4xl mx-auto text-center px-6 py-20">
                <div className="label-mono text-[#00FF41] mb-3">// saas licensing</div>
                <h1 className="text-4xl md:text-5xl font-display font-black tracking-tight mb-6 uppercase">
                    PROPELLING LOCALHOST <br className="hidden md:block" />
                    TO THE CLOUD
                </h1>
                <p className="text-[#A1A1AA] max-w-xl mx-auto leading-relaxed text-sm md:text-base">
                    Choose the plan that fits your pipeline. Secure HTTP tunnels, custom subdomains, custom domains, and live request inspections.
                </p>
            </section>

            {/* Plans Grid */}
            <section className="max-w-6xl mx-auto px-6 pb-24">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-[#27272A] divide-y md:divide-y-0 md:divide-x divide-[#27272A] bg-[#0A0A0A]">
                    
                    {/* Free Plan */}
                    <div className="p-8 flex flex-col justify-between">
                        <div>
                            <div className="flex items-center justify-between mb-4">
                                <span className="text-xs uppercase tracking-wider text-[#A1A1AA]">FREE TIER</span>
                                <Zap className="w-5 h-5 text-[#A1A1AA]" />
                            </div>
                            <div className="font-display text-4xl font-black text-[#FAFAFA] mb-6">
                                $0 <span className="text-xs text-[#52525B] font-mono">/ FOREVER</span>
                            </div>
                            <ul className="space-y-3.5 text-xs text-[#A1A1AA] mb-8">
                                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#00FF41] shrink-0" /> 1 Active Tunnel</li>
                                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#00FF41] shrink-0" /> Random Subdomain</li>
                                <li className="flex items-center gap-2 text-[#52525B]"><span className="text-red-500 font-bold">×</span> No Custom Domain</li>
                                <li className="flex items-center gap-2 text-[#52525B]"><span className="text-red-500 font-bold">×</span> No Password Protection</li>
                                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#00FF41] shrink-0" /> 500 requests / day</li>
                            </ul>
                        </div>
                        <Link to={user ? "/app/tunnels" : "/register"} className="tf-btn tf-btn-ghost w-full justify-center text-center mt-auto">
                            {user ? "CURRENT PLAN" : "GET STARTED"}
                        </Link>
                    </div>

                    {/* Pro Plan */}
                    <div className="p-8 flex flex-col justify-between bg-[#0F0F0F] relative border-y-2 border-y-[#00FF41] md:border-y-0 md:border-x-2 md:border-x-[#00FF41]">
                        <div className="absolute top-0 right-8 transform -translate-y-1/2 bg-[#00FF41] text-[#050505] text-[9px] font-black tracking-widest px-2 py-0.5 rounded-none uppercase">
                            RECOMMENDED
                        </div>
                        <div>
                            <div className="flex items-center justify-between mb-4">
                                <span className="text-xs uppercase tracking-wider text-[#00FF41] font-bold">DEVELOPER PRO</span>
                                <Flame className="w-5 h-5 text-[#00FF41]" />
                            </div>
                            <div className="font-display text-4xl font-black text-[#FAFAFA] mb-6">
                                $9 <span className="text-xs text-[#52525B] font-mono">/ MONTH</span>
                            </div>
                            <ul className="space-y-3.5 text-xs text-[#A1A1AA] mb-8">
                                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#00FF41] shrink-0" /> 5 Active Tunnels</li>
                                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#00FF41] shrink-0" /> Custom Subdomains</li>
                                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#00FF41] shrink-0" /> 1 Custom Domain</li>
                                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#00FF41] shrink-0" /> Password Protect (Basic Auth)</li>
                                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#00FF41] shrink-0" /> 50,000 requests / day</li>
                            </ul>
                        </div>
                        <button 
                            disabled={upgrading || user?.plan === "pro"}
                            onClick={() => handleUpgrade("pro_plan")} 
                            className="tf-btn w-full justify-center text-center mt-auto"
                        >
                            {user?.plan === "pro" ? "ACTIVE PLAN" : upgrading ? "INITIALIZING..." : "UPGRADE TO PRO"}
                        </button>
                    </div>

                    {/* Enterprise Plan */}
                    <div className="p-8 flex flex-col justify-between">
                        <div>
                            <div className="flex items-center justify-between mb-4">
                                <span className="text-xs uppercase tracking-wider text-[#A1A1AA]">SCALEUP ENTERPRISE</span>
                                <ShieldCheck className="w-5 h-5 text-[#FF9900]" />
                            </div>
                            <div className="font-display text-4xl font-black text-[#FAFAFA] mb-6">
                                $29 <span className="text-xs text-[#52525B] font-mono">/ MONTH</span>
                            </div>
                            <ul className="space-y-3.5 text-xs text-[#A1A1AA] mb-8">
                                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#00FF41] shrink-0" /> 100 Active Tunnels</li>
                                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#00FF41] shrink-0" /> Custom Subdomains</li>
                                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#00FF41] shrink-0" /> Unlimited Custom Domains</li>
                                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#00FF41] shrink-0" /> Password Protect (Basic Auth)</li>
                                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#00FF41] shrink-0" /> 5,000,000 requests / day</li>
                                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#00FF41] shrink-0" /> SLA & Priority Support</li>
                            </ul>
                        </div>
                        <button 
                            disabled={upgrading || user?.plan === "enterprise"}
                            onClick={() => handleUpgrade("enterprise_plan")} 
                            className="tf-btn tf-btn-ghost w-full justify-center text-center mt-auto border-[#FF9900] text-[#FF9900] hover:bg-[#FF9900] hover:text-[#050505]"
                        >
                            {user?.plan === "enterprise" ? "ACTIVE PLAN" : upgrading ? "INITIALIZING..." : "UPGRADE TO ENTERPRISE"}
                        </button>
                    </div>

                </div>
            </section>
        </PublicLayout>
    );
}
